/**
 * Admin-portal page for managing AI agents, skills, and channel
 * assignments. Each AI agent shadows a kind=ai users row; skills are
 * toggleable per scope; assignments wire agents onto channels.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Pencil, Plus } from "lucide-react";
import { z } from "zod";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  Input,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
  useZodForm,
} from "@lumika/ui";
import {
  AiAgentRespondToSchema,
  AiAgentStatusSchema,
  AiAssignmentMediumSchema,
  type AiAgent,
  type AiAgentChannelAssignment,
  type AiAgentRespondTo,
  type AiSkill,
} from "@lumika/shared";
import { adminApi } from "../../api/admin";

export function DealershipAiPage() {
  const { dealershipId } = useParams();
  if (!dealershipId) return null;
  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <PageHeader
        eyebrow="AI"
        title="Agents, skills, and routing"
        description="Each AI agent shadows a kind=ai user row. Skills are toggleable per scope; assignments wire agents onto channels."
      />
      <Tabs defaultValue="agents">
        <TabsList>
          <TabsTrigger value="agents">Agents</TabsTrigger>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          <TabsTrigger value="assignments">Channel assignments</TabsTrigger>
        </TabsList>
        <TabsContent value="agents">
          <AgentsSection dealershipId={dealershipId} />
        </TabsContent>
        <TabsContent value="skills">
          <SkillsSection dealershipId={dealershipId} />
        </TabsContent>
        <TabsContent value="assignments">
          <AssignmentsSection dealershipId={dealershipId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AgentsSection({ dealershipId }: { dealershipId: string }) {
  const qc = useQueryClient();
  const agents = useQuery({
    queryKey: ["admin", dealershipId, "ai-agents"],
    queryFn: () => adminApi.aiAgents(dealershipId),
  });
  const detail = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId),
  });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AiAgent | null>(null);
  // Exactly one kind=ai user per dealership, auto-provisioned at
  // dealership creation. The create dialog is a safety net only —
  // the primary path is editing the single existing Responder Agent.
  const aiUsers = (detail.data?.users ?? []).filter((u) => u.kind === "ai");
  const onSaved = () =>
    qc.invalidateQueries({ queryKey: ["admin", dealershipId, "ai-agents"] });
  return (
    <Section
      title={`${(agents.data ?? []).length} agents`}
      description="One Responder Agent per dealership. Click a row to configure model, voice, and routing."
      actions={
        <Button
          variant="ghost"
          onClick={() => setCreating(true)}
          disabled={aiUsers.length === 0 || (agents.data ?? []).length > 0}
          title={
            (agents.data ?? []).length > 0
              ? "Agent already provisioned"
              : aiUsers.length === 0
                ? "Create a kind=ai user first"
                : undefined
          }
        >
          <Plus className="h-4 w-4" /> Provision agent
        </Button>
      }
    >
      {agents.isLoading ? (
        <PageLoading message="Loading agents…" />
      ) : agents.isError ? (
        <PageError
          title="Couldn't load agents"
          description="Try refreshing the page."
        />
      ) : (agents.data ?? []).length === 0 ? (
        <EmptyState
          title="No Responder Agent yet"
          description="The agent is normally auto-provisioned when the dealership is created. Use Provision agent above only if it's missing."
        />
      ) : (
        <>
          <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Voice</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(agents.data ?? []).map((a) => (
                  <TableRow
                    key={a.id}
                    className="cursor-pointer"
                    onClick={() => setEditing(a)}
                  >
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell className="text-muted-foreground">{a.model}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {a.voiceListenModel ?? "—"} / {a.voiceSpeakModel ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={a.status === "active" ? "success" : "secondary"}>
                        {a.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit agent"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditing(a);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableBleed>
          {/* Mobile: condensed touch list with the same data hierarchy. */}
          <MobileList>
            {(agents.data ?? []).map((a) => (
              <MobileListRow
                key={a.id}
                onClick={() => setEditing(a)}
                primary={a.name}
                secondary={a.model}
                tertiary={`${a.voiceListenModel ?? "—"} / ${a.voiceSpeakModel ?? "—"}`}
                trailing={
                  <Badge variant={a.status === "active" ? "success" : "secondary"}>
                    {a.status}
                  </Badge>
                }
              />
            ))}
          </MobileList>
        </>
      )}
      {creating && (
        <AgentFormDialog
          mode="create"
          dealershipId={dealershipId}
          aiUsers={aiUsers}
          agent={null}
          onClose={() => setCreating(false)}
          onSaved={onSaved}
        />
      )}
      {editing && (
        <AgentFormDialog
          mode="edit"
          dealershipId={dealershipId}
          aiUsers={aiUsers}
          agent={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </Section>
  );
}

// The voice agent rides Twilio ConversationRelay, so the options are
// what THAT accepts — Deepgram for STT (transcription), ElevenLabs for
// TTS (ConversationRelay has no Deepgram TTS; its providers are
// Google/Amazon/ElevenLabs). Voice is admin-only, so these short
// static lists never reach a dealer UI. Values must stay in step with
// RELAY_SPEECH_MODELS / RELAY_DEFAULT_VOICE in
// apps/backend/src/telephony-webhooks/twiml.ts.
const DEEPGRAM_LISTEN_MODELS = [
  // Flux = Deepgram's conversational flagship (built-in end-of-turn
  // detection; 200–600 ms faster agent turns per Twilio) — the default.
  "flux",
  "nova-3-general",
  "nova-2-general",
] as const;
// ElevenLabs voice ids. The platform appends natural-conversation
// settings (RELAY_VOICE_TUNING in twiml.ts) to whichever bare id is
// stored here, so any pick gets the tuned, non-robotic read with no
// per-voice settings to manage. All are ElevenLabs premade/default
// voices (API-ready by id — no account add needed). Keep the first
// entry in step with RELAY_DEFAULT_VOICE in
// apps/backend/src/telephony-webhooks/twiml.ts.
// (Deepgram Aura voices like "Luna" are NOT selectable here —
// ConversationRelay's TTS is ElevenLabs/Google/Amazon only.)
const RELAY_VOICES = [
  { id: "EXAVITQu4vr4xnSDxMaL", label: "Sarah — female, warm & professional (default)" },
  { id: "cgSgspJ2msm6clMCkdW9", label: "Jessica — female, warm & upbeat" },
  { id: "XrExE9yKIg1WjnnlVkGX", label: "Matilda — female, calm / narration" },
  { id: "UgBBYS2sOqTuMpoF3BR0", label: "Mark — male, conversational" },
] as const;

// Every tool name the agent registry can build, i.e. the universe the
// per-channel excluded-tool lists filter. Page-local until a shared
// AGENT_TOOL_NAMES export exists (CLIENT_VISIBLE_TOOL_NAMES in
// @lumika/shared covers only the widget-visible subset). Must stay in
// step with the registry wiring in
// apps/backend/src/ai/ai-tenant-tools.registrar.ts plus brave_search in
// ai-tool-registry.factory.ts. (`hangup` / `get_skill_content` are wired
// outside the registry, so excluding them here would be a no-op.)
const AGENT_TOOL_NAMES = [
  "brave_search",
  "cancel_appointment",
  "cancel_task",
  "check_availability",
  "contact_info_autofill",
  "create_cards",
  "create_task",
  "defer_reply",
  "do_nothing",
  "escalate",
  "flag_for_human",
  "get_appointments",
  "get_dealership_team",
  "get_vehicle",
  "post_internal_note",
  "rooftop_context",
  "schedule_follow_up",
  "schedule_handoff_check",
  "search_facets",
  "search_vehicles",
  "send_sms",
  "submit_appointment",
  "submit_contact",
  "submit_tradein",
  "submit_vehicle",
  "update_appointment",
] as const;

/**
 * Human labels for the AI user's reply permission — the same four
 * inbox-visibility levels a human teammate has.
 */
const RESPOND_TO_LABEL: Record<AiAgentRespondTo, string> = {
  assigned_only: "Assigned to the AI only",
  assigned_and_unassigned: "Assigned to the AI + unassigned",
  rooftop_all: "Every lead in its rooftop(s)",
  group_all: "Every lead in the dealership",
};

const AgentFormSchema = z.object({
  userId: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  // `provider:model` composed at submit time. Provider drives the
  // Model select's option list; storing them separately in the form
  // means the cascade is reactive.
  provider: z.string().trim().min(1),
  model: z.string().trim().min(1),
  knowledgeAndBehavior: z.string().trim().min(1).max(20000),
  useBasePrompt: z.boolean(),
  status: AiAgentStatusSchema,
  respondTo: AiAgentRespondToSchema,
  allowInternet: z.boolean(),
  voiceListenModel: z.string().optional(),
  voiceSpeakModel: z.string().optional(),
  voiceThinkProvider: z.string().optional(),
  voiceThinkModel: z.string().optional(),
  // Per-channel persona + tool segregation (voice / SMS). Prompts are
  // free text; the excluded-tool fields hold comma-separated tool names
  // (edited via a checkbox group) parsed to string[] at submit.
  voicePrompt: z.string().max(20000).optional(),
  smsPrompt: z.string().max(20000).optional(),
  voiceExcludedTools: z.string().optional(),
  smsExcludedTools: z.string().optional(),
});

/** Parse a comma/newline-separated tool list into a string[], or null
 * when blank. Blank means "fall back to the code default" for voice and
 * "no SMS-specific withholding" for SMS. */
function parseToolList(raw: string | undefined): string[] | null {
  const tokens = (raw ?? "")
    .split(/[\n,]/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  return tokens.length > 0 ? tokens : null;
}

/**
 * Checkbox group over the known agent tool names, persisted in the form
 * as the same comma-separated string parseToolList() consumes at
 * submit. Stored names missing from the catalog (e.g. a legacy typo or
 * a renamed tool) are appended as extra rows so existing agents stay
 * editable instead of silently dropping the value.
 */
function ExcludedToolsField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const selected = parseToolList(value) ?? [];
  const known: readonly string[] = AGENT_TOOL_NAMES;
  const unknown = selected.filter((t) => !known.includes(t));
  function toggle(name: string) {
    const next = selected.includes(name)
      ? selected.filter((t) => t !== name)
      : [...selected, name];
    onChange(next.join(", "));
  }
  return (
    <div className="grid max-h-40 gap-1 overflow-y-auto rounded-md border border-border p-2">
      {[...AGENT_TOOL_NAMES, ...unknown].map((name) => (
        <label key={name} className="flex cursor-pointer items-center gap-2 text-xs">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded-sm border-input"
            checked={selected.includes(name)}
            onChange={() => toggle(name)}
          />
          <span className="font-mono">{name}</span>
          {!known.includes(name) && (
            <span className="text-muted-foreground">(unknown tool)</span>
          )}
        </label>
      ))}
    </div>
  );
}

function splitModel(raw: string): { provider: string; model: string } {
  const idx = raw.indexOf(":");
  if (idx < 0) return { provider: "anthropic", model: raw };
  return { provider: raw.slice(0, idx), model: raw.slice(idx + 1) };
}

function AgentFormDialog({
  mode,
  dealershipId,
  aiUsers,
  agent,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  dealershipId: string;
  aiUsers: { id: string; displayName: string | null; email: string | null }[];
  agent: AiAgent | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initial = (() => {
    if (mode === "edit" && agent) {
      const { provider, model } = splitModel(agent.model);
      return {
        userId: agent.userId,
        name: agent.name,
        provider,
        model,
        knowledgeAndBehavior: agent.knowledgeAndBehavior,
        useBasePrompt: agent.useBasePrompt,
        status: agent.status,
        respondTo: agent.respondTo,
        allowInternet: agent.allowInternet,
        voiceListenModel: agent.voiceListenModel ?? "",
        voiceSpeakModel: agent.voiceSpeakModel ?? "",
        voiceThinkProvider: agent.voiceThinkProvider ?? "",
        voiceThinkModel: agent.voiceThinkModel ?? "",
        voicePrompt: agent.voicePrompt ?? "",
        smsPrompt: agent.smsPrompt ?? "",
        voiceExcludedTools: (agent.voiceExcludedTools ?? []).join(", "),
        smsExcludedTools: (agent.smsExcludedTools ?? []).join(", "),
      };
    }
    return {
      userId: aiUsers[0]?.id ?? "",
      name: "",
      provider: "anthropic",
      model: "claude-sonnet-4-5",
      knowledgeAndBehavior: "",
      useBasePrompt: true,
      status: "draft" as const,
      respondTo: "assigned_and_unassigned" as const,
      allowInternet: false,
      voiceListenModel: "",
      voiceSpeakModel: "",
      voiceThinkProvider: "",
      voiceThinkModel: "",
      voicePrompt: "",
      smsPrompt: "",
      voiceExcludedTools: "",
      smsExcludedTools: "",
    };
  })();
  const form = useZodForm(AgentFormSchema, initial);
  const { values, errors, setField } = form;

  // Catalog — Provider list is one fetch; per-provider Model list is
  // a separate fetch keyed by provider, so changing the Provider
  // select reloads only what the user is about to need.
  const providers = useQuery({
    queryKey: ["admin", "model-providers"],
    queryFn: () => adminApi.modelProviders(),
  });
  const models = useQuery({
    queryKey: ["admin", "model-list", values.provider],
    queryFn: () => adminApi.listModels(values.provider),
    enabled: !!values.provider,
  });
  const voiceModels = useQuery({
    queryKey: ["admin", "model-list", values.voiceThinkProvider],
    queryFn: () => adminApi.listModels(values.voiceThinkProvider ?? ""),
    enabled: !!values.voiceThinkProvider,
  });

  // When provider changes (cascading select), default Model to the
  // first option in the new provider's list to avoid an empty / stale
  // selection. Edit mode preserves the stored provider:model pair even
  // when the live catalog no longer lists it — silently swapping the
  // production model on an unrelated edit is worse than keeping it.
  const modelsData = models.data;
  const currentModel = values.model;
  const currentProvider = values.provider;
  const initialModel = initial.model;
  const initialProvider = initial.provider;
  useEffect(() => {
    if (!modelsData || modelsData.length === 0) return;
    if (!modelsData.some((m) => m.id === currentModel)) {
      if (
        mode === "edit" &&
        currentProvider === initialProvider &&
        currentModel === initialModel
      ) {
        return;
      }
      setField("model", modelsData[0]!.id);
    }
  }, [
    mode,
    currentProvider,
    modelsData,
    currentModel,
    initialProvider,
    initialModel,
    setField,
  ]);

  const createMutation = useMutation({
    mutationFn: (input: z.infer<typeof AgentFormSchema>) =>
      adminApi.createAiAgent(dealershipId, {
        userId: input.userId,
        name: input.name,
        model: `${input.provider}:${input.model}`,
        knowledgeAndBehavior: input.knowledgeAndBehavior,
        useBasePrompt: input.useBasePrompt,
        status: input.status,
        isStreaming: false,
        allowInternet: input.allowInternet,
        responseDelaySeconds: 0,
        defaultSnoozeSeconds: 0,
        replyDelayAssignedSeconds: 0,
        replyDelayUnassignedSeconds: 300,
        replyDelayOtherAssigneeSeconds: 900,
        aiSuppressionSeconds: 2592000,
        respondTo: input.respondTo,
        responseSplitMode: "multiple",
        voiceListenModel: input.voiceListenModel?.trim() || null,
        voiceSpeakModel: input.voiceSpeakModel?.trim() || null,
        voiceThinkProvider: input.voiceThinkProvider?.trim() || null,
        voiceThinkModel: input.voiceThinkModel?.trim() || null,
        voicePrompt: input.voicePrompt?.trim() || null,
        smsPrompt: input.smsPrompt?.trim() || null,
        voiceExcludedTools: parseToolList(input.voiceExcludedTools),
        smsExcludedTools: parseToolList(input.smsExcludedTools),
      }),
    onSuccess: () => {
      onSaved();
      toast.success("Agent provisioned");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const updateMutation = useMutation({
    mutationFn: (input: z.infer<typeof AgentFormSchema>) =>
      adminApi.updateAiAgent(dealershipId, agent!.id, {
        name: input.name,
        model: `${input.provider}:${input.model}`,
        knowledgeAndBehavior: input.knowledgeAndBehavior,
        useBasePrompt: input.useBasePrompt,
        status: input.status,
        respondTo: input.respondTo,
        allowInternet: input.allowInternet,
        voiceListenModel: input.voiceListenModel?.trim() || null,
        voiceSpeakModel: input.voiceSpeakModel?.trim() || null,
        voiceThinkProvider: input.voiceThinkProvider?.trim() || null,
        voiceThinkModel: input.voiceThinkModel?.trim() || null,
        voicePrompt: input.voicePrompt?.trim() || null,
        smsPrompt: input.smsPrompt?.trim() || null,
        voiceExcludedTools: parseToolList(input.voiceExcludedTools),
        smsExcludedTools: parseToolList(input.smsExcludedTools),
      }),
    onSuccess: () => {
      onSaved();
      toast.success("Agent updated");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submit() {
    const r = form.validate();
    if (!r.ok) return;
    if (mode === "create") createMutation.mutate(r.data);
    else updateMutation.mutate(r.data);
  }

  const pending = createMutation.isPending || updateMutation.isPending;
  const modelMissing =
    Boolean(values.model) &&
    (modelsData ?? []).length > 0 &&
    !(modelsData ?? []).some((m) => m.id === values.model);
  const isDirty = JSON.stringify(values) !== JSON.stringify(initial);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Provision Responder Agent" : `Configure ${agent?.name ?? "agent"}`}
          </DialogTitle>
        </DialogHeader>
        <div className="grid max-h-[65vh] gap-5 overflow-y-auto -mx-1 px-1">
          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="col-span-full text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Identity
            </legend>
            {mode === "create" && (
              <FormField className="sm:col-span-2" label="Shadow user (kind=ai)" error={errors.userId}>
                <Select value={values.userId} onValueChange={(v) => setField("userId", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {aiUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.displayName ?? u.email ?? u.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            )}
            <FormField label="Display name" error={errors.name}>
              <Input value={values.name} onChange={(e) => setField("name", e.target.value)} />
            </FormField>
            <FormField label="Status" error={errors.status}>
              <Select
                value={values.status}
                onValueChange={(v) =>
                  setField("status", v as z.infer<typeof AgentFormSchema>["status"])
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AiAgentStatusSchema.options.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField
              label="Reply permission"
              description="Which leads the AI user auto-replies to — the same inbox-visibility levels a human teammate has."
              error={errors.respondTo}
            >
              <Select
                value={values.respondTo}
                onValueChange={(v) => setField("respondTo", v as AiAgentRespondTo)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AiAgentRespondToSchema.options.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {RESPOND_TO_LABEL[opt]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField
              label="Allow internet"
              description="When on, the assistant can call a Brave-powered web_search tool. Off by default — most dealership leads stay grounded in your inventory."
              error={errors.allowInternet}
            >
              <label className="flex cursor-pointer items-center gap-3 text-sm">
                <Switch
                  checked={values.allowInternet}
                  onCheckedChange={(v) => setField("allowInternet", v)}
                />
                <span>{values.allowInternet ? "Enabled" : "Disabled"}</span>
              </label>
            </FormField>
            <FormField
              label="Base BDC persona"
              description="When on (default), a polite, helpful, never-pushy BDC base persona is layered beneath the K&B so even a short prompt behaves well. The K&B overrides and extends it."
              error={errors.useBasePrompt}
            >
              <label className="flex cursor-pointer items-center gap-3 text-sm">
                <Switch
                  checked={values.useBasePrompt}
                  onCheckedChange={(v) => setField("useBasePrompt", v)}
                />
                <span>{values.useBasePrompt ? "Enabled" : "Disabled"}</span>
              </label>
            </FormField>
          </fieldset>

          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="col-span-full text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Chat model
            </legend>
            <FormField label="Provider" error={errors.provider}>
              <Select value={values.provider} onValueChange={(v) => setField("provider", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Loading…" />
                </SelectTrigger>
                <SelectContent>
                  {(providers.data ?? []).map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.displayName}
                      {p.hasApiKey ? "" : " · no key"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField
              label="Model"
              error={errors.model}
              description={
                modelMissing
                  ? "Stored model is not in the provider's current catalog — it's kept as-is until you pick another."
                  : undefined
              }
            >
              <Select value={values.model} onValueChange={(v) => setField("model", v)}>
                <SelectTrigger>
                  <SelectValue placeholder={models.isLoading ? "Loading…" : "Pick a model"} />
                </SelectTrigger>
                <SelectContent>
                  {modelMissing && (
                    <SelectItem value={values.model} disabled>
                      {values.model} (unavailable)
                    </SelectItem>
                  )}
                  {(models.data ?? []).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </fieldset>

          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="col-span-full text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Voice agent
            </legend>
            <p className="col-span-full text-xs text-muted-foreground">
              Admin-only. Listen is Deepgram speech-to-text; Voice is the
              ElevenLabs voice Twilio speaks with (the platform applies
              natural-conversation tuning to whichever voice you pick); Think is
              the LLM that drives in-call reasoning (often a faster provider than
              the chat model above). Leave blank for the platform defaults
              (Deepgram Flux + Sarah).
            </p>
            <FormField label="Listen model (Deepgram STT)" optional error={errors.voiceListenModel}>
              <Select
                value={values.voiceListenModel || "none"}
                onValueChange={(v) => setField("voiceListenModel", v === "none" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Platform default (flux)</SelectItem>
                  {DEEPGRAM_LISTEN_MODELS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Voice (ElevenLabs TTS)" optional error={errors.voiceSpeakModel}>
              <Select
                value={values.voiceSpeakModel || "none"}
                onValueChange={(v) => setField("voiceSpeakModel", v === "none" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Platform default (Sarah)</SelectItem>
                  {RELAY_VOICES.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Think provider" optional error={errors.voiceThinkProvider}>
              <Select
                value={values.voiceThinkProvider || "none"}
                onValueChange={(v) => {
                  if (v === "none") {
                    setField("voiceThinkProvider", "");
                    setField("voiceThinkModel", "");
                  } else {
                    setField("voiceThinkProvider", v);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Use env default</SelectItem>
                  {(providers.data ?? []).map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.displayName}
                      {p.hasApiKey ? "" : " · no key"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Think model" optional error={errors.voiceThinkModel}>
              <Select
                value={values.voiceThinkModel || "none"}
                onValueChange={(v) => setField("voiceThinkModel", v === "none" ? "" : v)}
                disabled={!values.voiceThinkProvider}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      values.voiceThinkProvider
                        ? voiceModels.isLoading
                          ? "Loading…"
                          : "Pick a model"
                        : "Pick provider first"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Use env default</SelectItem>
                  {(voiceModels.data ?? []).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </fieldset>

          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="col-span-full text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Channel personas — Phone &amp; SMS
            </legend>
            <p className="col-span-full text-xs text-muted-foreground">
              Admin-only. Phone (voice) and SMS are segregated channels with
              their own agent. Each prompt is appended on top of the
              dealership-wide K&amp;B for runs on that channel — leave blank to
              use the shared K&amp;B alone. Checked tools are withheld from that
              channel; with nothing checked, voice falls back to the code
              defaults (web search, inventory lookup, dealership team and
              scheduling tools are stripped on calls).
            </p>
            <FormField
              className="sm:col-span-2"
              label="Phone (voice) prompt"
              optional
              error={errors.voicePrompt}
            >
              <Textarea
                value={values.voicePrompt}
                onChange={(e) => setField("voicePrompt", e.target.value)}
                rows={4}
                placeholder="Voice-only guidance — e.g. keep turns to one sentence, always confirm the callback number aloud."
              />
            </FormField>
            <FormField label="Phone (voice) excluded tools" optional error={errors.voiceExcludedTools}>
              <ExcludedToolsField
                value={values.voiceExcludedTools ?? ""}
                onChange={(v) => setField("voiceExcludedTools", v)}
              />
            </FormField>
            <FormField label="SMS excluded tools" optional error={errors.smsExcludedTools}>
              <ExcludedToolsField
                value={values.smsExcludedTools ?? ""}
                onChange={(v) => setField("smsExcludedTools", v)}
              />
            </FormField>
            <FormField
              className="sm:col-span-2"
              label="SMS prompt"
              optional
              error={errors.smsPrompt}
            >
              <Textarea
                value={values.smsPrompt}
                onChange={(e) => setField("smsPrompt", e.target.value)}
                rows={4}
                placeholder="SMS-only guidance — e.g. keep it to one or two short sentences, no links unless asked."
              />
            </FormField>
          </fieldset>

          <fieldset className="grid gap-4">
            <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Knowledge &amp; Behavior
            </legend>
            <FormField
              label="Dealership-wide Knowledge &amp; Behavior"
              error={errors.knowledgeAndBehavior}
            >
              <Textarea
                value={values.knowledgeAndBehavior}
                onChange={(e) => setField("knowledgeAndBehavior", e.target.value)}
                rows={6}
                placeholder="Dealer identity, escalation policy, hand-off rules."
              />
            </FormField>
          </fieldset>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {mode === "edit" ? "Close" : "Cancel"}
          </Button>
          <Button onClick={submit} disabled={pending}>
            {pending ? "Saving…" : mode === "create" ? "Provision" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SkillsSection({ dealershipId }: { dealershipId: string }) {
  const qc = useQueryClient();
  const skills = useQuery({
    queryKey: ["admin", dealershipId, "ai-skills"],
    queryFn: () => adminApi.aiSkills(dealershipId),
  });
  const detail = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId),
  });
  const [open, setOpen] = useState(false);
  return (
    <Section
      title={`${(skills.data ?? []).length} skills`}
      description="Capabilities scoped by dealership / rooftop? / channel? — specificity wins."
      actions={
        <Button onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" /> New skill
        </Button>
      }
    >
      {skills.isLoading ? (
        <PageLoading message="Loading skills…" />
      ) : skills.isError ? (
        <PageError
          title="Couldn't load skills"
          description="Try refreshing the page."
        />
      ) : (skills.data ?? []).length === 0 ? (
        <EmptyState
          title="No skills"
          description="Tool/prompt_section/workflow capabilities for AI agents to invoke."
        />
      ) : (
        <ul className="grid gap-2">
          {(skills.data ?? []).map((s) => (
            <SkillRow key={s.id} skill={s} />
          ))}
        </ul>
      )}
      {open && (
        <SkillFormDialog
          dealershipId={dealershipId}
          rooftops={detail.data?.rooftops ?? []}
          onClose={() => setOpen(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ["admin", dealershipId, "ai-skills"] })}
        />
      )}
    </Section>
  );
}

function SkillRow({ skill }: { skill: AiSkill }) {
  return (
    <li className="rounded-md border border-border bg-card px-3 py-2 text-sm">
      <div className="flex items-center justify-between">
        <div className="font-medium">{skill.name}</div>
        <div className="text-xs text-muted-foreground">
          {skill.rooftopId ? "rooftop-scoped" : "dealership-wide"}
          {skill.isEnabled ? "" : " · disabled"}
        </div>
      </div>
      {skill.description ? (
        <div className="text-xs text-muted-foreground">{skill.description}</div>
      ) : null}
    </li>
  );
}

const SkillFormSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).optional(),
  // Skill content is real guidance — the backend enforces ≥200 chars.
  // Capped like the agent prompt fields (20000) so unbounded text
  // can't persist.
  content: z
    .string()
    .trim()
    .min(200, "Skill content must be at least 200 characters.")
    .max(20000),
  rooftopId: z.string().uuid().or(z.literal("all")),
  isEnabled: z.boolean(),
});

function SkillFormDialog({
  dealershipId,
  rooftops,
  onClose,
  onSaved,
}: {
  dealershipId: string;
  rooftops: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const initial: z.input<typeof SkillFormSchema> = {
    name: "",
    description: "",
    content: "",
    rooftopId: "all",
    isEnabled: true,
  };
  const form = useZodForm(SkillFormSchema, initial);
  const mutation = useMutation({
    mutationFn: (input: z.infer<typeof SkillFormSchema>) =>
      adminApi.createAiSkill(dealershipId, {
        name: input.name,
        description: input.description?.trim() || null,
        content: input.content,
        rooftopId: input.rooftopId === "all" ? null : input.rooftopId,
        isEnabled: input.isEnabled,
      }),
    onSuccess: () => {
      onSaved();
      toast.success("Skill saved");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  function submit() {
    const r = form.validate();
    if (r.ok) mutation.mutate(r.data);
  }
  const { values, errors, setField } = form;
  const contentLength = values.content?.trim().length ?? 0;
  const isDirty = JSON.stringify(values) !== JSON.stringify(initial);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>New AI skill</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <FormField label="Name" error={errors.name}>
            <Input value={values.name} onChange={(e) => setField("name", e.target.value)} />
          </FormField>
          <FormField label="Description" optional error={errors.description}>
            <Input
              value={values.description ?? ""}
              onChange={(e) => setField("description", e.target.value)}
            />
          </FormField>
          <FormField
            label="Content"
            error={errors.content}
            description={`The knowledge block injected into the agent's prompt. ${contentLength}/200 min characters.`}
          >
            <Textarea
              value={values.content ?? ""}
              onChange={(e) => setField("content", e.target.value)}
              rows={8}
              placeholder="Detailed guidance the agent should follow for this skill…"
            />
          </FormField>
          <FormField label="Rooftop scope" error={errors.rooftopId}>
            <Select value={values.rooftopId} onValueChange={(v) => setField("rooftopId", v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All rooftops</SelectItem>
                {rooftops.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentsSection({ dealershipId }: { dealershipId: string }) {
  const qc = useQueryClient();
  const assignments = useQuery({
    queryKey: ["admin", dealershipId, "ai-assignments"],
    queryFn: () => adminApi.aiAgentChannelAssignments(dealershipId),
  });
  const agents = useQuery({
    queryKey: ["admin", dealershipId, "ai-agents"],
    queryFn: () => adminApi.aiAgents(dealershipId),
  });
  const channels = useQuery({
    // Same key the Channels page uses, so a channel created there is
    // visible here without a hard refresh.
    queryKey: ["admin-bdc", dealershipId, "channels"],
    queryFn: () => adminApi.listChannels(dealershipId),
  });
  const detail = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId),
  });
  const [open, setOpen] = useState(false);
  return (
    <Section
      title={`${(assignments.data ?? []).length} assignments`}
      description="Which AI agent handles which channel on which rooftop."
      actions={
        <Button onClick={() => setOpen(true)} disabled={(agents.data ?? []).length === 0}>
          <Plus className="h-4 w-4" /> New assignment
        </Button>
      }
    >
      {assignments.isLoading ? (
        <PageLoading message="Loading assignments…" />
      ) : assignments.isError ? (
        <PageError
          title="Couldn't load assignments"
          description="Try refreshing the page."
        />
      ) : (assignments.data ?? []).length === 0 ? (
        <EmptyState
          title="No assignments"
          description="Wire agents onto channels to route inbound traffic."
        />
      ) : (
        <ul className="grid gap-2">
          {(assignments.data ?? []).map((a) => (
            <AssignmentRow
              key={a.id}
              assignment={a}
              agents={agents.data ?? []}
              channels={channels.data?.items ?? []}
            />
          ))}
        </ul>
      )}
      {open && (
        <AssignmentFormDialog
          dealershipId={dealershipId}
          agents={agents.data ?? []}
          channels={(channels.data?.items ?? []).map((c) => ({
            id: c.id,
            type: c.type,
            rooftopId: c.rooftopId,
            label: c.name,
          }))}
          rooftops={detail.data?.rooftops ?? []}
          onClose={() => setOpen(false)}
          onSaved={() =>
            qc.invalidateQueries({ queryKey: ["admin", dealershipId, "ai-assignments"] })
          }
        />
      )}
    </Section>
  );
}

function AssignmentRow({
  assignment,
  agents,
  channels,
}: {
  assignment: AiAgentChannelAssignment;
  agents: AiAgent[];
  channels: { id: string; name: string }[];
}) {
  const agent = agents.find((a) => a.id === assignment.aiAgentId);
  const channel = channels.find((c) => c.id === assignment.channelId);
  return (
    <li className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm">
      <div>
        <div className="font-medium">
          {agent?.name ?? assignment.aiAgentId} → {channel?.name ?? assignment.channelId}
        </div>
        <div className="text-xs text-muted-foreground">
          {assignment.medium ? `medium ${assignment.medium}` : "whole channel"}
          {assignment.isPrimary ? " · primary" : ""}
          {assignment.priority !== null ? ` · priority ${assignment.priority}` : ""}
        </div>
      </div>
    </li>
  );
}

const AssignmentFormSchema = z.object({
  rooftopId: z.string().uuid("Pick a rooftop."),
  aiAgentId: z.string().uuid(),
  channelId: z.string().uuid(),
  medium: z.union([z.literal(""), AiAssignmentMediumSchema]).optional(),
  isPrimary: z.boolean(),
});

function AssignmentFormDialog({
  dealershipId,
  agents,
  channels,
  rooftops,
  onClose,
  onSaved,
}: {
  dealershipId: string;
  agents: AiAgent[];
  channels: { id: string; type: string; rooftopId: string | null; label: string }[];
  rooftops: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // The assignment's rooftop is the channel's rooftop — the agent is
  // dealership-scoped and spans every rooftop.
  const form = useZodForm(AssignmentFormSchema, {
    // A channel assignment is always pinned to a real rooftop; group-wide
    // (rooftopId = null) channels leave this empty so the operator picks one.
    rooftopId: channels[0]?.rooftopId ?? "",
    aiAgentId: agents[0]?.id ?? "",
    channelId: channels[0]?.id ?? "",
    medium: "",
    isPrimary: false,
  });
  const mutation = useMutation({
    mutationFn: (input: z.infer<typeof AssignmentFormSchema>) =>
      adminApi.createAiAgentChannelAssignment(dealershipId, {
        rooftopId: input.rooftopId,
        aiAgentId: input.aiAgentId,
        channelId: input.channelId,
        medium: input.medium || null,
        isPrimary: input.isPrimary,
      }),
    onSuccess: () => {
      onSaved();
      toast.success("Assignment created");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  function submit() {
    const r = form.validate();
    if (r.ok) mutation.mutate(r.data);
  }
  const { values, errors, setField } = form;
  const selectedChannel = channels.find((c) => c.id === values.channelId);
  const selectedChannelGroupWide = !selectedChannel || selectedChannel.rooftopId === null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New channel assignment</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <FormField label="Agent" error={errors.aiAgentId}>
            <Select
              value={values.aiAgentId}
              onValueChange={(v) => setField("aiAgentId", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Channel" error={errors.channelId}>
            <Select
              value={values.channelId}
              onValueChange={(v) => {
                const channel = channels.find((c) => c.id === v);
                setField("channelId", v);
                // Always re-sync — a group-wide channel (rooftopId null)
                // must not inherit the previously selected channel's
                // rooftop; the operator picks one below instead.
                setField("rooftopId", channel?.rooftopId ?? "");
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {channels.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.label} ({c.type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          {selectedChannelGroupWide && (
            <FormField
              label="Rooftop"
              error={errors.rooftopId}
              description="This channel is group-wide — pin the assignment to one rooftop."
            >
              <Select
                value={values.rooftopId}
                onValueChange={(v) => setField("rooftopId", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a rooftop" />
                </SelectTrigger>
                <SelectContent>
                  {rooftops.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          )}
          <FormField label="Medium" optional error={errors.medium}>
            <Select
              value={values.medium || "none"}
              onValueChange={(v) =>
                setField("medium", v === "none" ? "" : (v as "sms" | "voice"))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Whole channel</SelectItem>
                {AiAssignmentMediumSchema.options.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
